import React, { useCallback, useEffect } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { useState } from 'react';
import MyDataTable from '../../../components/MyDataTable';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { RouteProp, useFocusEffect, useIsFocused, useRoute } from '@react-navigation/native';
import { ClonedPlateParamList } from '../../../typings/Params';
import { ClientError, GenericPaginatedParameters } from '../../../services/central-api/base-service';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { clonedPlateService } from '../../../services/central-api/cloned-plate';
import moment from 'moment';
import ClonedPlateModal from './ClonedPlateModal';
import Toast from 'react-native-toast-message';
import DeleteModal from '../../../components/DeleteModal';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import DataTableHeader from '../../../components/DataTableHeader';
import Card from '../../../components/Card';
import DataTableContainer from '../../../components/DataTableContainer';
import DropdownButton from '../../../components/DropdownButton';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import { RootNavigation } from '../../../Routes';

interface Props {
    navigation: RootNavigation;
}

export default function ClonedPlates({ navigation }: Props) {
    const route = useRoute<RouteProp<ClonedPlateParamList, 'ClonedPlates'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useState<GenericPaginatedParameters>({
        page: Math.max(route.params.page - 1, 0),
        textFilter: route.params.textFilter ?? '',
        limit: Number(route.params.limit) || 25,
    });

    const [clonePlates, setClonePlates] = useState<BaseClonedPlate[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [selectedClonedPlate, setClonedPlate] = useState<BaseClonedPlate>();
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['getPaginatedClonePlates', filters],
        queryFn: () => clonedPlateService.getPaginatedClonePlates(filters),
        enabled: !!authenticatedUser,
    });

    useEffect(() => {
        if (!data) {
            return;
        }
        setClonePlates(data.rows);
        setTotalItems(data.count);
    }, [data]);

    const deleteClonedPlate = async (clonedPlate: BaseClonedPlate) => {
        try {
            await clonedPlateService.deleteClonedPlate(clonedPlate.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            await refetch();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    };

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            setTotalItems(0);
            refetch();
        }
    }, [isFocused, filters]));


    return (
        <Container>
            <Header
                title={translate('clonedPlates')}
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredClonedPlates')}
                    zIndex={2}
                    filterInput={<FilterInput
                        value={textFilter ?? ''}
                        onChange={text => {
                            setTextFilter(text);
                        }}
                        placeholder={translate('typeToSearch')}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        onRowClicked={(row) => {
                            setClonedPlate(row);
                            setModalVisible(true);
                        }}
                        pointerOnHover
                        progressPending={isLoading}
                        columns={[{
                            name: 'ID',
                            selector: row => row.id,
                            grow: 0
                        },
                        {
                            name: translate('plate'),
                            selector: row => row.plate,
                            wrap: true,
                        },
                        {
                            name: translate('createdAt'),
                            selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm:ss'),
                            wrap: true,
                        },
                        {
                            name: translate('action'),
                            button: true,
                            cell: row => <DropdownButton actions={[{
                                content: translate('editInformation'),
                                onPress: () => {
                                    setClonedPlate(row);
                                    setModalVisible(true);
                                }
                            }, {
                                content: translate('delete'),
                                onPress: () => {
                                    setClonedPlate(row);
                                    setDeleteModalVisible(true);
                                },
                                variant: 'danger',
                            }]} />
                        }]}
                        data={clonePlates}
                    />}
                    pagination={<Pagination
                        totalItems={totalItems}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>

            {
                selectedClonedPlate ? <DeleteModal
                    setModalVisible={setDeleteModalVisible}
                    isModalVisible={isDeleteModalVisible}
                    itemName={selectedClonedPlate.plate}
                    onSubmit={() => deleteClonedPlate(selectedClonedPlate)}
                /> : <></>
            }

            {
                selectedClonedPlate ? <ClonedPlateModal
                    onClose={() => {
                        setClonedPlate(undefined);
                        setModalVisible(false);
                        refetch();
                    }}
                    isModalVisible={isModalVisible}
                    plateData={selectedClonedPlate}
                /> : <></>
            }
        </Container>
    );
}
